import React, { useEffect, useState, useRef } from 'react';
import { adminService } from '../../services/admin.service';
import Badge from '../../components/common/Badge';

const AdminTrading: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  
  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetTdId, setTargetTdId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Default date to current local time for datetime-local input
  const getCurrentLocalDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({ 
    userId: '', 
    asset: 'BTC', 
    investment: '', 
    result: 'WIN', 
    payout: '',
    tdDate: getCurrentLocalDateTime()
  });
  
  // Searchable Dropdown States for adding new trades
  const [userSearch, setUserSearch] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'WIN' | 'LOSS' | 'PENDING'>('ALL');

  // Frontend Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchTradings = async () => {
    try {
      const res = await adminService.getTradings();
      setItems(Array.isArray(res) ? res : (res?.data || []));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await adminService.getUsers();
      setUsers(Array.isArray(res) ? res : (res?.data || []));
    } catch (err) {
      console.error("User Fetch Error:", err);
    }
  };

  useEffect(() => {
    fetchTradings();
    fetchUsers();

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const numericUserId = parseInt(form.userId, 10);
    if (isNaN(numericUserId) || numericUserId <= 0) {
      alert("CRITICAL ERROR: Invalid Operator Node. Please re-select a user from the dropdown.");
      return;
    }

    setProcessing(-1);
    try {
      const payload = {
        tdDate: form.tdDate,
        asset: form.asset,
        investment: parseFloat(form.investment),
        result: form.result,
        payout: parseFloat(form.payout),
        td_customer: String(numericUserId),
      };

      const res = await adminService.addTrading(payload);
      
      if (res.status === false) {
        throw new Error(res.message);
      }

      setShowAdd(false);
      resetForm();
      await fetchTradings();
    } catch (err: any) {
      alert(`Injection Failed: ${err.message || 'Check network connection'}`);
    } finally {
      setProcessing(null);
    }
  };

  const resetForm = () => {
    setForm({ 
      userId: '', 
      asset: 'BTC', 
      investment: '', 
      result: 'WIN', 
      payout: '',
      tdDate: getCurrentLocalDateTime()
    });
    setUserSearch('');
    setSelectedUserName('');
  };

  const openDeleteModal = (tdId: number) => {
    setTargetTdId(tdId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!targetTdId) return;
    
    setIsDeleting(true);
    try {
      const res = await adminService.deleteTrading(targetTdId);
      if (res.status) {
        await fetchTradings();
        setShowDeleteModal(false);
        setTargetTdId(null);
      } else {
        throw new Error(res.message || "Deletion protocol failure");
      }
    } catch (err: any) {
      alert(`PURGE FAILED: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredItems = items.filter(t => {
    const userMatch = (
      t.fname?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      t.lname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.userId?.toString().includes(searchQuery)
    );
    
    const assetMatch = t.asset?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'ALL' || t.result === filter;
    return (userMatch || assetMatch) && matchesFilter;
  });

  const filteredUsers = users.filter(u => 
    u.fname?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.lname?.toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.userId || u.id)?.toString().includes(userSearch)
  );

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) return <div className="h-96 flex items-center justify-center"><div className="w-12 h-12 border-4 border-purple-600 border-t-transparent animate-spin rounded-full"></div></div>;

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 px-2">
        <div>
          <h2 className="text-4xl font-black text-white uppercase tracking-tighter">Operation Logs</h2>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em] mt-2">Historical Trade Ledger</p>
        </div>
        <div className="flex flex-col md:flex-row gap-4 w-full lg:max-w-3xl">
           <input 
            type="text" 
            placeholder="Search Operator, Email, UID or Asset..."
            value={searchQuery}
            onChange={(e) => {setSearchQuery(e.target.value); setCurrentPage(1);}}
            className="flex-1 bg-slate-900/50 border border-white/5 rounded-2xl px-6 py-4 text-white text-xs font-bold outline-none focus:ring-2 focus:ring-purple-600/40"
          />
          <select 
            value={filter} 
            onChange={e => {setFilter(e.target.value as any); setCurrentPage(1);}}
            className="bg-slate-900/80 border border-white/5 rounded-2xl px-6 py-4 text-white text-[10px] font-black uppercase tracking-widest outline-none"
          >
            <option value="ALL">Outcome: All</option>
            <option value="WIN">WIN (Profit)</option>
            <option value="LOSS">LOSS (Debit)</option>
            <option value="PENDING">PENDING</option>
          </select>
          <button 
            onClick={() => { resetForm(); setShowAdd(true); }}
            className="bg-purple-600 text-white font-black px-8 py-4 rounded-2xl transition-all shadow-xl shadow-purple-600/30 text-[10px] uppercase tracking-widest active:scale-95 whitespace-nowrap"
          >
            Inject Operation
          </button>
        </div>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-3xl rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-600 uppercase text-[9px] tracking-[0.2em] font-black border-b border-white/5">
              <tr>
                <th className="px-10 py-8">Operator Node</th>
                <th className="px-10 py-8">Asset Matrix</th>
                <th className="px-10 py-8">Invest / Payout</th>
                <th className="px-10 py-8">Outcome</th>
                <th className="px-10 py-8">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {currentItems.length > 0 ? currentItems.map((t) => {
                const isActive = t.activeStatus === true || t.activeStatus === 1;

                return (
                  <tr key={t.tdId} className="hover:bg-purple-600/5 transition-colors group">
                    <td className="px-10 py-6">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-white font-bold text-sm tracking-tight">
                            {t.fname} {t.lname}
                          </p>
                          {t.aga === 1 && (
                            <svg className="w-3.5 h-3.5 text-amber-500 fill-current" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          )}
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-rose-500 shadow-[0_0_8px_#f43f5e]'}`}></span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-mono tracking-tight">{t.email}</p>
                        <p className="text-[9px] text-slate-600 font-black uppercase tracking-widest">UID: {t.userId || t.id}</p>
                      </div>
                    </td>
                    <td className="px-10 py-6 font-black text-slate-400 text-xs tracking-widest uppercase">{t.asset} / USDT</td>
                    <td className="px-10 py-6">
                      <p className="text-white font-black text-xs">${Number(t.investment).toFixed(2)}</p>
                      <p className={`font-black text-[10px] mt-0.5 ${t.result === 'WIN' ? 'text-emerald-500' : 'text-slate-600'}`}>
                        {t.result === 'WIN' ? `+$${Number(t.payout).toFixed(2)}` : '-$0.00'}
                      </p>
                    </td>
                    <td className="px-10 py-6">
                      <Badge label={t.result} variant={t.result === 'WIN' ? 'success' : t.result === 'LOSS' ? 'error' : 'warning'} />
                    </td>
                    <td className="px-10 py-6">
                      <button 
                        disabled={processing === t.tdId}
                        onClick={() => openDeleteModal(t.tdId)}
                        title="Purge Operation"
                        className="text-slate-700 hover:text-rose-500 transition-colors p-2.5 bg-slate-950/40 rounded-xl border border-white/5 shadow-inner hover:border-rose-500/30"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="px-10 py-32 text-center text-slate-500 uppercase text-[10px] font-black tracking-widest">
                    No matching operation logs
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="px-10 py-8 bg-slate-950/40 border-t border-white/5 flex items-center justify-between">
            <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Page {currentPage} of {totalPages}</span>
            <div className="flex space-x-2">
               <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-5 py-2.5 bg-slate-900 border border-white/5 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white disabled:opacity-30" disabled={currentPage === 1}>Prev</button>
               <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-5 py-2.5 bg-slate-900 border border-white/5 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white disabled:opacity-30" disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-xl animate-in fade-in duration-300">
           <div className="bg-slate-900 border border-rose-500/20 w-full max-w-md p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden text-center">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-rose-500/10 rounded-full blur-3xl"></div>
              
              <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-rose-500/20 shadow-inner">
                <svg className="w-10 h-10 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>

              <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Purge Authorization</h3>
              <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8">
                You are about to permanently excise operation <span className="text-white font-mono">TX-{targetTdId}</span> from the global ledger. This action is irreversible.
              </p>

              <div className="flex flex-col gap-3">
                <button 
                  onClick={confirmDelete} 
                  disabled={isDeleting}
                  className="w-full py-5 bg-rose-600 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-xl shadow-rose-600/30 hover:bg-rose-500 transition-all active:scale-95 disabled:opacity-30"
                >
                  {isDeleting ? 'Excising Record...' : 'Confirm Purge'}
                </button>
                <button 
                  onClick={() => setShowDeleteModal(false)} 
                  disabled={isDeleting}
                  className="w-full py-5 bg-slate-800 text-slate-400 font-black uppercase text-[10px] tracking-widest rounded-2xl hover:bg-slate-700 transition-all active:scale-95"
                >
                  Abort Protocol
                </button>
              </div>
           </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-xl animate-in zoom-in-95">
          <div className="bg-slate-900 border border-white/5 w-full max-w-xl p-12 rounded-[3rem] shadow-2xl relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-600/10 rounded-full blur-3xl"></div>
            
            <h3 className="text-3xl font-black text-white uppercase tracking-tight mb-2">Operation Injection</h3>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-10">Manually record a distributed asset trade</p>
            
            <form onSubmit={handleAdd} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="group relative" ref={dropdownRef}>
                  <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 ml-2">Operator Node Selection</label>
                  <div 
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className={`w-full bg-slate-950 border rounded-2xl px-5 py-4 text-white font-bold cursor-pointer flex items-center justify-between transition-all ${isUserDropdownOpen ? 'border-purple-500' : 'border-slate-800'}`}
                  >
                    <span className={selectedUserName ? 'text-white' : 'text-slate-600 truncate mr-2'}>
                      {selectedUserName || "Search Operator..."}
                    </span>
                    <svg className={`w-4 h-4 text-slate-500 transition-transform flex-shrink-0 ${isUserDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {isUserDropdownOpen && (
                    <div className="absolute top-full left-0 w-full mt-2 bg-slate-900 border border-white/5 rounded-2xl shadow-2xl z-[110] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="p-3 border-b border-white/5">
                        <input 
                          type="text" 
                          autoFocus
                          placeholder="Search name or ID..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-white text-xs outline-none focus:border-purple-500"
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto custom-scrollbar">
                        {filteredUsers.length > 0 ? filteredUsers.map((u) => {
                          const uid = u.userId || u.id;
                          return (
                            <div 
                              key={uid}
                              onClick={() => {
                                setForm({ ...form, userId: uid.toString() });
                                setSelectedUserName(`${u.fname} ${u.lname}`);
                                setIsUserDropdownOpen(false);
                              }}
                              className="px-5 py-3 hover:bg-purple-600/10 cursor-pointer flex items-center justify-between group/user transition-colors"
                            >
                              <div className="min-w-0 flex-1 pr-4">
                                <p className="text-white text-sm font-bold group-hover/user:text-purple-400 truncate">{u.fname} {u.lname}</p>
                                <p className="text-[10px] text-slate-500 font-mono truncate">{u.email}</p>
                              </div>
                              <span className="text-[10px] font-black bg-slate-950 px-2 py-1 rounded text-slate-400 border border-white/5 flex-shrink-0">#{uid}</span>
                            </div>
                          );
                        }) : (
                          <div className="px-5 py-10 text-center text-slate-600 text-[10px] font-black uppercase tracking-widest">No nodes found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="group">
                  <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 ml-2">Asset Node</label>
                  <select value={form.asset} onChange={e => setForm({...form, asset: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-5 py-4 text-white font-bold outline-none appearance-none cursor-pointer">
                    <option>BTC</option><option>ETH</option><option>SOL</option><option>USDT</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="group">
                  <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 ml-2">Investment ($)</label>
                  <input type="number" step="0.01" required value={form.investment} onChange={e => setForm({...form, investment: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-5 py-4 text-white font-bold outline-none focus:ring-2 focus:ring-purple-600/40" placeholder="0.00" />
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 ml-2">Expected Outcome</label>
                  <select value={form.result} onChange={e => setForm({...form, result: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-5 py-4 text-white font-bold outline-none cursor-pointer">
                    <option value="WIN">WIN (PROFIT)</option><option value="LOSS">LOSS (DEBIT)</option><option value="PENDING">PENDING</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="group">
                  <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 ml-2">Payout Amount ($)</label>
                  <input type="number" step="0.01" required value={form.payout} onChange={e => setForm({...form, payout: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-5 py-4 text-white font-bold outline-none focus:ring-2 focus:ring-purple-600/40" placeholder="0.00" />
                </div>
                <div className="group">
                  <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 ml-2">Trade Execution Date</label>
                  <input 
                    type="datetime-local" 
                    required 
                    value={form.tdDate} 
                    onChange={e => setForm({...form, tdDate: e.target.value})} 
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-5 py-4 text-white font-bold outline-none focus:ring-2 focus:ring-purple-600/40"
                  />
                </div>
              </div>

              <div className="flex space-x-4 pt-8">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 bg-slate-800 text-slate-400 font-black py-5 rounded-2xl text-[10px] tracking-widest uppercase transition-all">Abort</button>
                <button type="submit" disabled={processing === -1 || !form.userId} className="flex-2 bg-purple-600 text-white font-black py-5 rounded-2xl text-[10px] tracking-widest uppercase shadow-xl shadow-purple-600/30 active:scale-95 transition-all disabled:opacity-30">Sync to Ledger</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTrading;